import { Copy, Check, Activity, Users, BarChart3, TrendingUp } from "lucide-react";
import { useTokenData } from "@/context/TokenDataContext";
import { STATUS_LABEL } from "@/config/tokenData";
import { formatCompactUsd, formatInt, formatPercent, formatPrice } from "@/lib/format";
import { SolanaLogo } from "@/components/brand/SolanaLogo";
import { Logo } from "@/components/brand/Logo";
import { LaunchBadge } from "@/components/LaunchBadge";
import { FadeIn } from "@/components/motion/Reveal";
import { HOME_PLATFORM_LINKS, KBULL_TOKEN_ADDRESS } from "@/config/buy";
import { PlatformLinks } from "@/components/PlatformLinks";
import { useAddressClipboard } from "@/components/buy/useAddressClipboard";

const Stat = ({ icon: Icon, label, value, change, testId, accent = "text-neon", loading }) => (
  <div className="flex items-center gap-3 px-4 py-3 sm:px-5" data-testid={testId}>
    <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-md border border-neon/40 bg-neon/5 ${accent}`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0 leading-none">
      <div className="eyebrow !text-[0.6rem]">{label}</div>
      <div className="mt-1.5 font-mono text-[1.05rem] font-bold tracking-tight text-white sm:text-[1.15rem]" data-testid={`${testId}-value`} aria-busy={loading || undefined}>
        {loading ? <span className="skeleton h-[1.05rem] w-24" /> : value}
      </div>
      {change !== undefined && !loading && (
        <div className={`mt-1 font-mono text-[0.68rem] ${change >= 0 ? "text-neon" : "text-rose-400"}`}>
          {formatPercent(change)}
        </div>
      )}
    </div>
  </div>
);

export const TokenBar = () => {
  const { token, marketCap, isLoading, isError, isSimulated, isPreLaunch } = useTokenData();
  const address = KBULL_TOKEN_ADDRESS;
  const isTba = !address;
  const statusLabel = STATUS_LABEL[token?.status] ?? STATUS_LABEL.LAUNCH_PENDING;
  const { state: copyState, copy: copyAddress } = useAddressClipboard(address, !isTba);
  const copied = copyState === "copied";

  const dash = "—";

  return (
    <FadeIn delay={0.35} className="container-x pt-[88px]" data-testid="token-bar">
      <div className="glass glass-neon hud scanlines relative overflow-hidden rounded-xl" style={{ "--hud-color": "#00FF66" }}>
        <div className="grid grid-cols-2 divide-y divide-white/8 md:grid-cols-4 md:divide-y-0 xl:grid-cols-[1.9fr_1fr_1fr_1fr_1fr]">
          <div className="col-span-2 flex items-center gap-3 px-4 py-3 md:col-span-4 md:border-b md:border-white/8 xl:col-span-1 xl:border-b-0 xl:border-r sm:px-5">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full ring-1 ring-neon/40 shadow-[0_0_14px_rgba(0,255,102,0.35)]">
              <Logo size={44} priority testId="token-bar-logo" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-display text-[0.78rem] font-bold tracking-wider text-white" data-testid="token-bar-title">
                  $KBULL TOKEN <span className="inline-flex items-center gap-1 text-neon">(<SolanaLogo size={11} />SOLANA)</span>
                </span>
                {isPreLaunch && <LaunchBadge testId="token-bar-status-badge" />}
                {isSimulated && (
                  <span className="rounded border border-cyan2/60 bg-cyan2/10 px-1.5 py-0.5 font-mono text-[0.55rem] tracking-widest text-cyan2" data-testid="token-bar-simulation-badge">
                    SIMULATION
                  </span>
                )}
                {isError && (
                  <span className="rounded border border-rose-400/50 bg-rose-400/10 px-1.5 py-0.5 font-mono text-[0.55rem] tracking-widest text-rose-300" data-testid="token-bar-error-badge">
                    DATA UNAVAILABLE
                  </span>
                )}
              </div>
              <div className="mt-1 truncate font-mono text-[0.72rem] text-white/70" data-testid="token-address" title={address}>
                {isLoading ? <span className="skeleton h-3 w-60" /> : isTba ? `STATUS: ${statusLabel} · CONTRACT ADDRESS ANNOUNCED AT LAUNCH` : address}
              </div>
            </div>
            <button
              type="button"
              onClick={copyAddress}
              className="inline-flex h-10 shrink-0 items-center gap-2 rounded-md border border-neon/50 bg-neon/10 px-3 font-mono text-[0.65rem] font-bold tracking-wide text-neon transition-[background-color,box-shadow] hover:bg-neon/20 hover:shadow-[0_0_16px_rgba(0,255,102,0.4)]"
              aria-label="Copy token address"
              data-testid="token-address-copy-button"
              data-state={copyState}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span>{copied ? "COPIED!" : "COPY ADDRESS"}</span>
            </button>
          </div>

          <div className="border-white/8 md:border-r">
            <Stat icon={Activity} label="PRICE" value={token ? formatPrice(token.currentPrice) : dash} change={token?.priceChange24h} testId="token-price-display" loading={isLoading} />
          </div>
          <div className="border-l border-white/8 md:border-l-0 md:border-r">
            <Stat icon={TrendingUp} label="MARKET CAP" value={marketCap !== null ? formatCompactUsd(marketCap) : dash} change={isSimulated ? undefined : token?.marketCapChange24h} testId="token-mcap-display" accent="text-gold" loading={isLoading} />
          </div>
          <div className="border-white/8 md:border-r">
            <Stat icon={Users} label="HOLDERS" value={token ? formatInt(token.holders) : dash} testId="token-holders-display" accent="text-cyan2" loading={isLoading} />
          </div>
          <div className="border-l border-white/8 md:border-l-0">
            <Stat icon={BarChart3} label="24H VOLUME" value={token ? formatCompactUsd(token.volume24h) : dash} testId="token-volume-display" loading={isLoading} />
          </div>
        </div>
      </div>
      {copyState === "error" && <p role="alert" className="mt-2 text-xs text-rose-300" data-testid="token-address-copy-error">COPY UNAVAILABLE — SELECT THE ADDRESS TO COPY IT.</p>}
      <PlatformLinks platforms={HOME_PLATFORM_LINKS} placement="home" />
    </FadeIn>
  );
};
